import React, {
    useContext, useEffect, useState, useCallback
} from 'react';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { WizardPage, wizardProps, WizardPageTemplate } from 'gw-portals-wizard-react';
import {
    ViewModelServiceContext,
    withViewModelService,
    ViewModelForm
} from 'gw-portals-viewmodel-react';
import { ModalNextProvider } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { useValidation } from 'gw-portals-validation-react';
import Claim from '../../models/Claim';
import metadata from './ContactDetailsPage.metadata.json5';
import styles from './ContactDetailsPage.module.scss';
import messages from '../../FNOL.messages';

function FNOLContactDetailsPage(props) {
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const { wizardData: claimVM, updateWizardData, updateWizardSnapshot } = props;
    const [contactDetailsVM, updateContactDetailsVM] = useState();
    const [contactPerson, setContactPersonValue] = useState({ reporterType: 'differentPerson' });

    const {
        onValidate,
        isComponentValid,
        initialValidation,
        registerComponentValidation
    } = useValidation('FNOLContactDetailsPage');

    const initializeVM = useCallback((isReporter) => {
        let contactDTORequest = {};
        const reporterContact = _.find(_.get(claimVM.value, 'relatedContacts', {}), { role: 'reporter' }) || {};
        if (isReporter) {
            if (!_.isEmpty(reporterContact)) {
                contactDTORequest = {
                    role: 'reporter',
                    contact: {
                        primaryAddress: {
                            country: 'US'
                        },
                        ..._.get(reporterContact, 'contact', {})
                    },
                };
            } else {
                ModalNextProvider.showAlert({
                    title: messages.reporterNotFound,
                    message: messages.reporterNotFoundMessage,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: messages.dialogOk
                }).then(() => {
                    return _.noop;
                }, _.noop);
            }
        } else {
            const isMainContactAvailable = !_.isEmpty(_.get(claimVM.value, 'mainContact', {})) && (_.get(claimVM.value, 'mainContact.publicID') !== _.get(reporterContact, 'contact.publicID'));
            const contact = isMainContactAvailable ? _.get(claimVM.value, 'mainContact') : {};
            contactDTORequest = {
                role: 'reporter',
                contact: {
                    primaryAddress: {
                        country: 'US'
                    },
                    ...contact
                }
            };
        }
        const submittingClaimVM = viewModelService.create(
            new Claim(contactDTORequest),
            'cc',
            'edge.capabilities.claim.fnol.dto.FnolRelatedContactDTO',
            {
                ContactDetailReporter: true
            },
        );
        const newClaimVM = viewModelService.clone(submittingClaimVM);
        updateContactDetailsVM(newClaimVM);
        return newClaimVM;
    }, [claimVM, viewModelService]);

    const onRadioButtonValueChange = useCallback((value, path) => {
        const dateClone = _.cloneDeep(contactPerson);
        _.set(dateClone, path, value);
        if (value === 'reporter') {
            initializeVM(true);
        } else {
            initializeVM(false);
        }
        setContactPersonValue(dateClone);
    }, [contactPerson, initializeVM]);

    const isContactNumberCheck = useCallback(() => {
        return (_.get(contactDetailsVM, 'contact.aspects.valid')
            && _.get(contactDetailsVM, 'contact.aspects.subtreeValid'));
    }, [contactDetailsVM]);

    useEffect(
        () => {
            const isReporter = _.get(claimVM.value, 'sameAsReporter', false);
            const type = isReporter ? 'reporter' : 'differentPerson';
            setContactPersonValue({ reporterType: type });
            initializeVM(isReporter);
        },
        // execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => {
        registerComponentValidation(isContactNumberCheck);
    }, [isContactNumberCheck, registerComponentValidation]);

    const updateContactVM = useCallback(() => {
        const clonedContact = _.clone(contactDetailsVM.value);
        const displayName = `${_.get(clonedContact, 'contact.firstName', '')} ${_.get(clonedContact, 'contact.lastName', '')}`;
        _.set(clonedContact, 'contact.displayName', displayName);
        _.set(claimVM.value, 'mainContact', _.get(clonedContact, 'contact'));
        _.set(claimVM.value, 'sameAsReporter', _.get(contactPerson, 'reporterType') === 'reporter');
        return claimVM;
    }, [claimVM, contactDetailsVM, contactPerson]);

    const onNext = useCallback(
        () => {
            const cloneClaimVM = updateContactVM();
            updateWizardSnapshot(cloneClaimVM);
            updateWizardData(cloneClaimVM);
        },
        [updateContactVM, updateWizardData, updateWizardSnapshot]
    );

    const onCancel = useCallback(
        () => {
            if (isComponentValid) {
                updateWizardData(updateContactVM());
            } else {
                _.set(claimVM.value, 'sameAsReporter', _.get(contactPerson, 'reporterType') === 'reporter');
                updateWizardData(claimVM);
            }
        },
        [claimVM, contactPerson, isComponentValid, updateContactVM, updateWizardData]
    );

    const overrides = {
        '@field': {
            showOptional: true,
            disabled: _.get(contactPerson, 'reporterType') === 'reporter'
        },
        workNumberField: {
            required: _.get(contactDetailsVM, 'contact.primaryPhoneType.value.code') === 'work'
        },
        homeNumberField: {
            required: _.get(contactDetailsVM, 'contact.primaryPhoneType.value.code') === 'home'
        },
        mobileNumberField: {
            required: _.get(contactDetailsVM, 'contact.primaryPhoneType.value.code') === 'mobile'
        },
        firstNameField: {
            visible: !(_.get(contactDetailsVM, 'contact.role.value.code') === 'insured')
        },
        lastNameField: {
            visible: !(_.get(contactDetailsVM, 'contact.role.value.code') === 'insured')
        },
        addressLineField: {
            visible: !(_.get(contactDetailsVM, 'contact.role.value.code') === 'insured')
        },
        newContactCityField: {
            readOnly: _.get(contactDetailsVM, 'contact.role.value.code') === 'insured'
        },
        newContactZipCodeField: {
            readOnly: _.get(contactDetailsVM, 'contact.role.value.code') === 'insured'
        },
        newContactStateField: {
            readOnly: _.get(contactDetailsVM, 'contact.role.value.code') === 'insured'
        },
        typeSelection: {
            value: contactPerson.reporterType,
            disabled: false
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onRadioButtonValueChange: onRadioButtonValueChange
        }
    };

    const writeValue = useCallback(
        (value, path) => {
            const clonedContact = _.clone(contactDetailsVM);
            _.set(clonedContact, path, value);
            updateContactDetailsVM(clonedContact);
            updateWizardData(claimVM);
        },
        [claimVM, contactDetailsVM, updateWizardData]
    );

    if (_.isEmpty(contactDetailsVM)) {
        return null;
    }

    return (
        <WizardPage
            cancelLabel={translator(messages.fnolSaveandExit)}
            disableNext={!(isComponentValid)}
            skipWhen={initialValidation}
            template={WizardPageTemplate}
            onNext={onNext}
            onCancel={onCancel}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={contactDetailsVM}
                overrideProps={overrides}
                onValueChange={writeValue}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
                onValidationChange={onValidate}
            />
        </WizardPage>
    );
}

FNOLContactDetailsPage.propTypes = wizardProps;
export default withAuthenticationContext(withRouter(withViewModelService(FNOLContactDetailsPage)));
